// Alpine `mentionTextarea`: @-mention picker for any plain <textarea>.
// Rendered by shared/_mention_textarea.html.erb.
//
// The visible textarea [data-mention-input] has no name and shows readable
// text ("@Ada Lovelace"). A hidden [data-mention-store] input carries the real
// param name and the serialized text with tokens ("@[Ada Lovelace](user:…)"),
// so every submit path (Turbo form, FormData, manual fetch) posts tokens
// without a submit-time rewrite.
//
// While the popup is open, ArrowUp/ArrowDown/Enter/Tab/Escape are handled in
// the capture phase on the component root and stopped, so a host handler like
// "@keydown.enter → send" never fires mid-pick. Enter only counts as a pick
// when an option is active: with "No matches" showing it closes the popup and
// still reaches the host (the chat composer sends).
//
// Screen readers hear result counts through `announcement`, bound to an
// always-present aria-live region outside the popup.
//
// With a record context (and `teamUrl`), the first row is the "Deal team"
// shortcut (mention_picker.js#dealTeamItem): picking it inserts one ordinary
// "@Name" mention per team member not already mentioned, so the writer sees
// exactly who was added. The first *person* stays the default selection, so
// "@" + Enter still picks one colleague; ArrowUp reaches the team row.
//
// Below the textarea, shared/_mention_audience.html.erb lists who a save will
// notify and warns about mentioned colleagues who can't see the record
// (mention_audience.js). Mentions already in the text when the form loaded
// were notified by an earlier save and are left out.
import { detectQuery, parseTokens, sanitizeLabel, serialize, safeAvatarUrl, toDisplay } from "./mention_tokens.js";
import {
  dealTeamInsertedAnnouncement,
  dealTeamItem,
  fetchDealTeam,
  fetchMentionables,
  pickerAnnouncement
} from "./mention_picker.js";
import { audienceState } from "./mention_audience.js";
import { followUpState } from "./mention_follow_up.js";

const DEBOUNCE_MS = 150;

export function mentionTextareaData(config = {}) {
  return {
    url: config.url || "/mentionables",
    contextType: config.contextType || null,
    contextId: config.contextId || null,
    minChars: config.minChars ?? 2,
    resetOnSubmit: !!config.resetOnSubmit,
    listboxId: config.listboxId || `mention-listbox-${Math.random().toString(36).slice(2, 10)}`,
    messages: config.messages || {},
    lookupUrl: config.lookupUrl || null,
    teamUrl: config.teamUrl || null,
    currentUserId: config.currentUserId || null,
    audience: config.audience !== false,

    ...audienceState(),
    // "Ask @X to follow up" (shared/_mention_follow_up), when rendered.
    ...followUpState(),

    open: false,
    announcement: "",
    results: [],
    activeIndex: -1,
    searched: false,
    tokens: [],
    _query: null,
    _timer: null,
    _abort: null,
    _silent: false,
    // Everything init() assigns must be declared here. Alpine writes an
    // undeclared key to the *outermost* x-data scope (e.g. the layout's
    // <main x-data>), so two pickers on one page would share one `input`.
    input: null,
    store: null,
    _form: null,
    _onInput: null,
    _onKeydown: null,
    _onBlur: null,
    _onClickCaret: null,
    _onSubmitEnd: null,

    get activeId() {
      return this.open && this.activeIndex >= 0 ? `${this.listboxId}-${this.activeIndex}` : "";
    },

    audienceConfig() {
      return {
        enabled: this.audience,
        lookupUrl: this.lookupUrl,
        currentUserId: this.currentUserId,
        messages: this.messages,
        contextType: this.contextType,
        contextId: this.contextId
      };
    },

    init() {
      this.input = this.$root.querySelector("[data-mention-input]");
      this.store = this.$root.querySelector("[data-mention-store]");
      if (!this.input || !this.store) return;

      this.tokens = parseTokens(this.store.value);
      this.rememberInitialAudience(this.tokens);
      this.refreshAudience(this.tokens);

      this._onInput = () => {
        this.sync();
        if (!this._silent) this.detect();
      };
      this._onKeydown = (event) => this.handleKeydown(event);
      // Deferred so a click on an option lands first; skipped when focus has
      // come back to the textarea meanwhile (a momentary blur, e.g. a
      // programmatic focus elsewhere, must not drop an open picker).
      this._onBlur = () => setTimeout(() => this.closeUnlessFocused(), 150);
      this._onClickCaret = () => this.detect();
      this.input.addEventListener("input", this._onInput);
      this.input.addEventListener("blur", this._onBlur);
      this.input.addEventListener("click", this._onClickCaret);
      this.$root.addEventListener("keydown", this._onKeydown, true);

      this._form = this.input.form || this.$root.closest("form");
      if (this._form && this.resetOnSubmit) {
        this._onSubmitEnd = (event) => {
          if (event.detail?.success !== false) this.reset();
        };
        this._form.addEventListener("turbo:submit-end", this._onSubmitEnd);
      }
    },

    destroy() {
      if (!this.input) return;
      this.input.removeEventListener("input", this._onInput);
      this.input.removeEventListener("blur", this._onBlur);
      this.input.removeEventListener("click", this._onClickCaret);
      this.$root.removeEventListener("keydown", this._onKeydown, true);
      if (this._form && this._onSubmitEnd) this._form.removeEventListener("turbo:submit-end", this._onSubmitEnd);
      clearTimeout(this._timer);
      if (this._abort) this._abort.abort();
    },

    // Public: the stored (token) value, for hosts that post manually.
    serialized() {
      this.sync();
      return this.store ? this.store.value : "";
    },

    // Public: replace the content with stored (token) text, e.g. when a host
    // carries a rich-text note over into this textarea. Fires "input" so
    // listeners such as x-autosize re-measure, without opening the picker.
    setValue(stored) {
      if (!this.input || !this.store) return;
      const value = String(stored ?? "");
      this.close();
      this.tokens = parseTokens(value);
      this.input.value = toDisplay(value);
      this._silent = true;
      try {
        this.sync();
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
      } finally {
        this._silent = false;
      }
    },

    // Public: re-point the picker at another record (e.g. once a discussable
    // has been chosen in the new-thread form).
    setContext(type, id) {
      this.contextType = type || null;
      this.contextId = id || null;
      if (this.store) this.refreshAudience(parseTokens(this.store.value));
    },

    sync() {
      if (!this.input || !this.store) return;
      const value = serialize(this.input.value, this.tokens);
      if (this.store.value !== value) {
        this.store.value = value;
        this.store.dispatchEvent(new Event("change", { bubbles: true }));
      }
      // From the stored text, so a mention whose text was deleted drops out.
      this.refreshAudience(parseTokens(value));
    },

    detect() {
      const found = detectQuery(this.input.value, this.input.selectionStart, this.tokens);
      const hasContext = !!(this.contextType && this.contextId);
      if (!found || (!hasContext && found.query.length < this.minChars)) {
        this.close();
        return;
      }
      this._query = found;
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.search(found.query), DEBOUNCE_MS);
    },

    async search(q) {
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      try {
        const signal = this._abort.signal;
        const [results, members] = await Promise.all([
          fetchMentionables(this.url, {
            q,
            contextType: this.contextType,
            contextId: this.contextId,
            signal
          }),
          this.contextType && this.contextId
            ? fetchDealTeam(this.teamUrl, { contextType: this.contextType, contextId: this.contextId })
            : []
        ]);
        // A newer keystroke has started its own search meanwhile.
        if (signal.aborted) return;
        const people = results.map((item) => ({ ...item, avatar_url: safeAvatarUrl(item.avatar_url) }));
        const team = dealTeamItem(members, { query: q, exclude: this.mentionedIds(), messages: this.messages });
        this.results = team ? [team, ...people] : people;
        this.searched = true;
        // The first person, not the team row, is the default pick.
        this.activeIndex = people.length ? (team ? 1 : 0) : team ? 0 : -1;
        // "No matches" only for a real query; a two-word query with no match
        // is most likely ordinary prose.
        this.open = this.results.length > 0 || (q.length >= this.minChars && !q.includes(" "));
        this.announcement = this.open ? pickerAnnouncement(team, people.length, this.messages) : "";
      } catch (error) {
        if (error?.name !== "AbortError") this.close();
      }
    },

    handleKeydown(event) {
      if (!this.open) return;
      const stop = () => {
        event.preventDefault();
        event.stopPropagation();
      };
      switch (event.key) {
        case "ArrowDown":
          stop();
          if (this.results.length) this.activeIndex = (this.activeIndex + 1) % this.results.length;
          break;
        case "ArrowUp":
          stop();
          if (this.results.length)
            this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
          break;
        case "Enter":
        case "Tab":
          if (this.activeIndex >= 0 && this.results[this.activeIndex]) {
            stop();
            this.pick(this.results[this.activeIndex]);
          } else {
            // Nothing to pick: close and let the key through (Enter sends).
            this.close();
          }
          break;
        case "Escape":
          stop();
          this.close();
          break;
      }
    },

    // Ids mentioned in the text right now, plus the writer: the deal-team
    // shortcut never adds these again.
    mentionedIds() {
      const ids = this.store ? parseTokens(serialize(this.input?.value ?? "", this.tokens)).map((t) => t.id) : [];
      if (this.currentUserId) ids.push(this.currentUserId);
      return ids;
    },

    pick(item) {
      if (!item || !this._query) return;
      const picked = (item.kind === "team" ? item.members : [item])
        .map((person) => ({ id: person.id, label: sanitizeLabel(person.name) }))
        .filter((person) => person.id && person.label);
      if (!picked.length) return;
      const value = this.input.value;
      const caret = this.input.selectionStart ?? value.length;
      const insert = `${picked.map((person) => `@${person.label}`).join(" ")} `;
      this.input.value = value.slice(0, this._query.start) + insert + value.slice(caret);
      const position = this._query.start + insert.length;
      this.input.setSelectionRange(position, position);
      picked.forEach((person) => this.tokens.push({ type: "user", id: person.id, label: person.label }));
      this.close();
      this.sync();
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
      this.input.focus();
      // After the input event, whose detect() closes (and clears) the live region.
      if (item.kind === "team") this.announcement = dealTeamInsertedAnnouncement(item, this.messages);
    },

    closeUnlessFocused() {
      const active = typeof document === "undefined" ? null : document.activeElement;
      if (this.input && active === this.input) return;
      this.close();
    },

    close() {
      this.open = false;
      this.announcement = "";
      this.results = [];
      this.activeIndex = -1;
      this.searched = false;
      this._query = null;
      clearTimeout(this._timer);
    },

    reset() {
      this.tokens = [];
      if (this.input) this.input.value = "";
      if (this.store) this.store.value = "";
      this.close();
      // A fresh message: nobody counts as notified-by-an-earlier-save.
      this.audienceInitialIds = [];
      this.refreshAudience([]);
    }
  };
}

export default function registerMentionTextarea(Alpine) {
  Alpine.data("mentionTextarea", mentionTextareaData);
}
